/**
 * 랜딩 "해봄이란?" 섹션. 무엇을/어떻게/어떻게 활용을 캡처와 함께 지그재그로 보여준다.
 */
import React from "react";

type Feature = {
  /** "A가 아니라,". 대조의 앞부분 */
  title: string;
  /** 대조의 뒷부분. 브랜드 컬러로 강조됩니다(`.hl`) */
  highlight: string;
  body: React.ReactNode;
  points: string[];
  image: { src: string; alt: string };
  /** true 면 이미지가 왼쪽. 지그재그를 만드는 좌우 반전 (모바일에선 CSS order 로 항상 글이 먼저) */
  reverse?: boolean;
};

const FEATURES: Feature[] = [
  {
    title: "흥미가 아니라,",
    highlight: "미래 역량",
    body: (
      <>
        AI가 직업을 바꿔도, 잠재력이 터질 강점 진로 분야는 남습니다. 해봄은 흥미·성격이 아니라 미래
        역량을 기준으로 강점을 진단해, 어디서 시작하면 좋을지 알려줍니다.
      </>
    ),
    points: [
      "미래 역량 6대 유형·12 코어 스킬·30 진로 갈래 (WEF·2022개정 교과 핵심역량 기반)",
      "6개 마을 캐릭터 유형으로 강점을 한눈에",
    ],
    image: {
      src: "/images/landing/sec2-competency.jpg",
      alt: "해봄 6대 역량 마을 캐릭터 유형",
    },
  },
  {
    title: "자기보고식이 아니라,",
    highlight: "논·서술형",
    body: (
      <>
        스스로 체크하는 자기보고식은 &lsquo;이상적인 나&rsquo;를 답하기 쉽습니다. 해봄은 게임처럼
        몰입하는 시나리오 속 문제를 직접 풀게 하여, 실제 문제 해결 과정으로 더 객관적으로 역량을
        확인합니다.
      </>
    ),
    points: [
      "게임형 100% 논·서술형 (40분 몰입형 스토리 미션)",
      "AI(Multi-LLM) 1차 채점 + 전문가 검증 하이브리드",
    ],
    image: {
      src: "/images/landing/sec2-assessment.jpg",
      alt: "길잡이별 검사 화면 예시 (게임형 논서술형 미션)",
    },
    reverse: true,
  },
  {
    title: "사라질 직업이 아니라,",
    highlight: "미래 진로",
    body: (
      <>
        기존의 단순 직업 추천에서 멈추지 않습니다. 강점을 2030·2040 미래 진로로 연결하고, 앞으로
        나아갈 방향까지 제시합니다.
      </>
    ),
    points: [
      "2030·2040 미래 직업 매칭 (AI 대체율 포함)",
      "학생·교사(학부모) 리포트 2종 + AI 액션 플랜 수업 활용 가이드 제공 (4~8차시)",
    ],
    image: {
      src: "/images/landing/sec2-report.jpg",
      alt: "해봄 결과 리포트 예시 (강점 분석·미래 직업·코칭 가이드)",
    },
  },
];

function FeatureText({ feature }: { feature: Feature }) {
  return (
    <div className="feature-text">
      <h3>
        {feature.title} <span className="hl">{feature.highlight}</span>
      </h3>
      <p>{feature.body}</p>
      <ul className="feature-points">
        {feature.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  );
}

function FeatureVisual({ image }: { image: Feature["image"] }) {
  return (
    <div className="feature-visual">
      {/* next/image 는 output:"export" 에서 최적화가 꺼져 이점이 없어 img 그대로 씁니다 */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={image.src} alt={image.alt} loading="lazy" />
    </div>
  );
}

/** 화면 2. 해봄이란? 무엇을/어떻게/어떻게 활용을 캡처와 함께 지그재그로 보여 줍니다. */
export default function LandingAbout() {
  return (
    <section className="section" id="about">
      <div className="shell">
        <div className="sec2-head">
          <h2 className="t-h2">그래서, 해봄은 이렇게 합니다</h2>
          <p className="t-body">
            단순 자기보고식 검사가 아닌, 시나리오 속 문제 해결 과정으로 미래 역량을 진단하고, AI시대에
            맞는 진로 방향을 제시합니다.
          </p>
        </div>

        {FEATURES.map((feature) =>
          feature.reverse ? (
            <div className="feature-row reverse" key={feature.title}>
              <FeatureVisual image={feature.image} />
              <FeatureText feature={feature} />
            </div>
          ) : (
            <div className="feature-row" key={feature.title}>
              <FeatureText feature={feature} />
              <FeatureVisual image={feature.image} />
            </div>
          )
        )}
      </div>
    </section>
  );
}
